import classNames from 'classnames';
import React, { useState } from 'react';

import { getZonedDate } from '@eika-infrastruktur/date';
import { IconTypes } from '@eika/icon';

import { useConfig } from '../../../../defaultConfig';
import { usePaymentAccountContext } from '../../../../hooks/useAccountState';
import useTranslation from '../../../../hooks/useTranslation';
import { useSigningContext } from '../../../../signingContext/signingContext';
import { EikaModal } from '../../../EikaModal';
import {
    ReceiptInput,
    OwntextInput,
    DateSelect,
    AmountInput,
    ReceiverComboBox,
    FromAccountSelect,
} from '../../../NettbankCreateTransactions/components';
import useReceiverSelector from '../../../NettbankCreateTransactions/components/ReceiverComboBox/useReceiverSelector';
import { MessageInput } from '../../../fields';
import { AccountFreeWithdrawalLabel } from '../../../shared/AccountFreeWithdrawalLabel';
import DesktopDropdownPlaceholder from '../../DesktopDropdownPlaceholder';
import { SelectedReceiver } from '../../SelectedReceiver';
import NewReceiver from '../SelectReceiver/NewReceiver';
import { PaymentActionType } from '../form/actions';
import useFormContext from '../form/useFormContext';
import { isNoCoverage } from '../form/validate';
import { FieldPropsForDesktop } from './FieldProps';

const PaymentDesktopFields: React.FC<FieldPropsForDesktop> = ({
    locked,
    freeWithdrawals,
    receiverState,
    kid,
    date,
    isFuturePayment,
    animateModalAs,
    isActive = true,
    useDesktopPlaceholder = true,
    hideInstant,
    inFocusModal,
    paymentLimitsWarning,
    isLoading,
    validAccounts,
    invalidAccounts,
}) => {
    const { signingOperationPending } = useSigningContext();
    const {
        searchString: [searchString, setSearchString],
        showAddNewReciever: [showAddReceiver, setShowAddReceiver],
        state,
        errors,
        handleChange,
        curryChange,
    } = useFormContext();

    const { targetFromAccount } = usePaymentAccountContext();
    const translated = useTranslation();

    const { featureToggles } = useConfig();

    const [placeholderHeight, setPlaceholderHeight] = useState(0);

    const showLock = locked || signingOperationPending;

    const { toAccountErrorMessage, selectReceiver, ocr, receivers, waitingForReceivers } = useReceiverSelector(
        { value: state.toAccount, error: errors.toAccount || errors.immediate },
        curryChange<SelectedReceiver | undefined>(PaymentActionType.TO_ACCOUNT_SELECTED),
        setSearchString,
        locked,
    );

    const kidOrMessageIsLoading =
        kid.isLoading || (state.toAccount && typeof state.toAccount.kidRequired === 'undefined');
    return (
        <>
            <FromAccountSelect
                className={classNames('transaction-form__from-account-selector', {
                    error: errors.accountId || errors.toAccount || toAccountErrorMessage,
                    'has-withdrawal': freeWithdrawals.show,
                    open: isActive,
                })}
                id="payment-from-account"
                label={translated.from}
                items={validAccounts ?? []}
                invalidItems={invalidAccounts}
                onSelect={(item) => {
                    handleChange({
                        type: PaymentActionType.FROM_ACCOUNT_SELECTED,
                        payload: item,
                    });
                }}
                placeholder={translated.payFromAccount}
                locked={showLock}
                isLoading={isLoading}
                selected={state.fromAccount}
                selectedAccount={targetFromAccount}
                error={
                    errors.accountId
                        ? { className: 'transaction-form__from-account-error', message: errors.accountId }
                        : undefined
                }
                bottomLabel={
                    freeWithdrawals.show ? (
                        <AccountFreeWithdrawalLabel
                            className="transaction-form__from-account-withdrawal"
                            freeWithdrawals={freeWithdrawals.data}
                        />
                    ) : undefined
                }
                isPaymentModalOpen
                setPlaceholderHeight={setPlaceholderHeight}
                tabIndex={isActive ? undefined : -1}
            />
            <ReceiverComboBox
                className={classNames('transaction-form__to-account-selector', {
                    error: errors.toAccount || toAccountErrorMessage || errors.accountId || errors.immediate,
                    'has-withdrawal': freeWithdrawals.show,
                    open: isActive,
                })}
                id="payment-to-account"
                data-testid="payment-to-account"
                label={translated.to}
                placeholder={translated.addReceiver}
                searchString={
                    searchString.length === 0 && state.toAccount?.accountName
                        ? state.toAccount?.accountName
                        : searchString
                }
                setSearchString={(value: string) => setSearchString(value)}
                icon={IconTypes.PERSONER_24}
                items={receivers ?? []}
                onSelect={(item?: SelectedReceiver) => {
                    selectReceiver(item);
                }}
                selected={state.toAccount}
                locked={showLock || (receiverState.lock ?? false)}
                isLoading={waitingForReceivers || ocr.isPending}
                onShowAddReceiver={() => setShowAddReceiver(true)}
                addReceiverText={translated.addNewReceiver}
                error={toAccountErrorMessage}
                isPaymentModalOpen
                setPlaceholderHeight={setPlaceholderHeight}
                tabIndex={isActive ? undefined : -1}
            />
            {isActive && (
                <>
                    <AmountInput
                        className={classNames('transaction-form__amount-selector', {
                            error: errors.amount || errors.amountLimit,
                        })}
                        value={state.amount}
                        label={translated.payAmount}
                        error={errors.amount}
                        limitError={errors.amountLimit}
                        limitWarning={paymentLimitsWarning}
                        onChange={(value) => handleChange({ type: PaymentActionType.AMOUNT, payload: value })}
                        isNoCoverage={isNoCoverage(state, validAccounts)}
                        locked={showLock}
                        currency={state.currency}
                    />
                    <MessageInput
                        className={classNames('transaction-form__message-selector', { error: errors.note })}
                        noteValue={state.note}
                        kidValue={state.kid?.value}
                        error={errors.note || errors.kid}
                        isLoading={kidOrMessageIsLoading}
                        selectedReceiver={state.toAccount}
                        onChange={(value) => handleChange({ type: PaymentActionType.NOTE, payload: value })}
                        onChangeKid={(val) =>
                            handleChange({ type: PaymentActionType.SET_KID, payload: { value: val } })
                        }
                        locked={showLock || kid.lock}
                    />
                    <DateSelect
                        className={classNames('transaction-form__date-selector', { error: errors.paymentDate })}
                        minDate={date.minDate}
                        date={state.paymentDate ?? getZonedDate()}
                        onChange={(date) => handleChange({ type: PaymentActionType.PAYMENT_DATE, payload: date })}
                        error={errors.paymentDate}
                        locked={showLock}
                        onlyBusinessDays
                        immediate={state.immediate}
                        onChangeImmediate={(immediate: boolean) =>
                            handleChange({
                                type: PaymentActionType.SET_IMMEDIATE,
                                payload: immediate,
                            })
                        }
                        hideInstant={hideInstant}
                        useFocusModal={inFocusModal}
                    />

                    {!featureToggles.disableOwnText && (
                        <OwntextInput
                            className="transaction-form__owntext-selector"
                            ownText={state.ownText}
                            locked={showLock}
                            onChangeOwntext={(value) =>
                                handleChange({
                                    type: PaymentActionType.OWNTEXT,
                                    payload: value,
                                })
                            }
                        />
                    )}
                    {!isFuturePayment && (
                        <ReceiptInput
                            className="transaction-form__receipt-selector"
                            locked={showLock}
                            receiptType={state.receiptType}
                            onChangeReceipt={(value) =>
                                handleChange({
                                    type: PaymentActionType.SET_RECEIPT_TYPE,
                                    payload: value,
                                })
                            }
                        />
                    )}
                </>
            )}

            {useDesktopPlaceholder && <DesktopDropdownPlaceholder height={placeholderHeight} />}
            {!inFocusModal && (
                <EikaModal
                    id="payment-add-new-receiver-modal"
                    open={showAddReceiver}
                    setOpen={setShowAddReceiver}
                    animateAs={animateModalAs}
                >
                    <NewReceiver
                        receivers={receivers}
                        searchString={searchString}
                        onChange={(selectedReceiver) => {
                            setShowAddReceiver(false);
                            setSearchString(selectedReceiver.accountName ?? '');
                            handleChange({
                                type: PaymentActionType.TO_ACCOUNT_SELECTED,
                                payload: selectedReceiver,
                            });
                        }}
                        isNettbank
                        onCancel={() => setShowAddReceiver(false)}
                    />
                </EikaModal>
            )}
        </>
    );
};

export default PaymentDesktopFields;
